import type { LogbookEntry } from '@/lib/db';
import { mapColumns, parseCsv, stringifyCsv } from '@/lib/parse/csv';

import { blankApproaches, blankLandings, blankTakeoffs, blankTimeMatrix } from '../entry';
import { formatDecimalHours, parseDurationHours } from '../time';
import { splitRoute } from './route';
import type { ImportParseResult, ParsedLogbookRow } from './types';

/**
 * MyFlightbook's CSV import format.
 *
 * MyFlightbook's own importer documentation (`myflightbook.com/logbook/mvc/pub/ImportTable`,
 * not reachable from this app's build environment to verify against directly
 * — `docs/PHASE-6.md`) is long-standing and matches names widely documented
 * elsewhere: `Date`, `AircraftID`, `Route`, `TotalFlightTime`, `PIC`, `SIC`,
 * `CFI`, `Dual Received`, `Night`, `IMC`, `SimulatedIMC`, `GroundSim`,
 * `FullStopLandings`, `NightLandings`, `Comments`. This is a documented
 * best-effort subset, not a verified round trip against a live MyFlightbook
 * account — `columns.ts`-style flexible header matching (case/spacing
 * insensitive, common synonyms) is what makes it tolerant of the variants
 * that show up in practice, but a pilot's own export is still worth checking
 * against the imported drafts before approving them.
 */

const HEADER = [
  'Date',
  'AircraftID',
  'Route',
  'TotalFlightTime',
  'PIC',
  'SIC',
  'CFI',
  'Dual Received',
  'Night',
  'IMC',
  'SimulatedIMC',
  'GroundSim',
  'FullStopLandings',
  'NightLandings',
  'Comments',
] as const;

export function exportMyFlightbookCsv(entries: readonly LogbookEntry[]): string {
  const rows: (string | number)[][] = [[...HEADER]];
  for (const entry of entries) {
    rows.push([
      entry.date,
      entry.registration ?? '',
      [entry.from, ...(entry.intermediate ?? []), entry.to]
        .filter((part) => part !== undefined && part !== '')
        .join(' '),
      formatDecimalHours(entry.time.dayHr + entry.time.nightHr),
      formatDecimalHours(entry.time.picHr),
      formatDecimalHours(entry.time.coPilotHr),
      formatDecimalHours(entry.time.instructorHr),
      formatDecimalHours(entry.time.dualHr),
      formatDecimalHours(entry.time.nightHr),
      formatDecimalHours(entry.time.actualInstrumentHr),
      formatDecimalHours(entry.time.simulatedInstrumentHr),
      formatDecimalHours(entry.time.groundInstrumentHr),
      entry.landings.day + entry.landings.night,
      entry.landings.night,
      entry.remarks ?? '',
    ]);
  }
  return stringifyCsv(rows);
}

type Field =
  | 'date'
  | 'aircraftId'
  | 'route'
  | 'totalFlightTime'
  | 'pic'
  | 'sic'
  | 'cfi'
  | 'dualReceived'
  | 'night'
  | 'imc'
  | 'simulatedImc'
  | 'groundSim'
  | 'fullStopLandings'
  | 'nightLandings'
  | 'comments';

const SYNONYMS: Readonly<Record<Field, readonly string[]>> = {
  date: ['date'],
  aircraftId: ['aircraftid', 'aircraft id', 'tail number'],
  route: ['route'],
  totalFlightTime: ['totalflighttime', 'total flight time'],
  pic: ['pic'],
  sic: ['sic'],
  cfi: ['cfi'],
  dualReceived: ['dual received', 'dualreceived'],
  night: ['night'],
  imc: ['imc', 'actual instrument'],
  simulatedImc: ['simulatedimc', 'simulated imc', 'hood'],
  groundSim: ['groundsim', 'ground sim', 'sim'],
  fullStopLandings: ['fullstoplandings', 'full stop landings', 'landings'],
  nightLandings: ['nightlandings', 'night landings'],
  comments: ['comments', 'remarks'],
};

function cell(row: readonly string[], index: number): string {
  return index === -1 ? '' : (row[index] ?? '');
}

function hoursOrZero(raw: string): number {
  if (raw.trim() === '') {
    return 0;
  }
  return parseDurationHours(raw) ?? 0;
}

function intOrZero(raw: string): number {
  const parsed = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function importMyFlightbookCsv(text: string): ImportParseResult {
  const csvRows = parseCsv(text);
  const [header, ...dataRows] = csvRows;
  if (header === undefined) {
    return { rows: [], errors: [] };
  }
  const columns = mapColumns(header, SYNONYMS);
  if (columns.date === -1) {
    return {
      rows: [],
      errors: [{ line: 1, message: 'No "Date" column found in this file.' }],
    };
  }

  const rows: ParsedLogbookRow[] = [];
  const errors: ImportParseResult['errors'] = [];

  dataRows.forEach((row, index) => {
    const line = index + 2;
    const date = cell(row, columns.date).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      errors.push({ line, message: `"${date}" is not a date in YYYY-MM-DD form.` });
      return;
    }

    const nightHr = hoursOrZero(cell(row, columns.night));
    const totalHr = hoursOrZero(cell(row, columns.totalFlightTime));
    const dayHr = Math.max(0, totalHr - nightHr);
    const totalLandings = intOrZero(cell(row, columns.fullStopLandings));
    const nightLandings = Math.min(totalLandings, intOrZero(cell(row, columns.nightLandings)));

    rows.push({
      date,
      ...(cell(row, columns.aircraftId).trim() === ''
        ? {}
        : { registration: cell(row, columns.aircraftId).trim().toUpperCase() }),
      ...splitRoute(cell(row, columns.route)),
      ...(cell(row, columns.comments).trim() === ''
        ? {}
        : { remarks: cell(row, columns.comments).trim() }),
      approaches: blankApproaches(),
      time: {
        ...blankTimeMatrix(),
        dayHr,
        nightHr,
        picHr: hoursOrZero(cell(row, columns.pic)),
        coPilotHr: hoursOrZero(cell(row, columns.sic)),
        instructorHr: hoursOrZero(cell(row, columns.cfi)),
        dualHr: hoursOrZero(cell(row, columns.dualReceived)),
        actualInstrumentHr: hoursOrZero(cell(row, columns.imc)),
        simulatedInstrumentHr: hoursOrZero(cell(row, columns.simulatedImc)),
        groundInstrumentHr: hoursOrZero(cell(row, columns.groundSim)),
      },
      landings: {
        ...blankLandings(),
        day: Math.max(0, totalLandings - nightLandings),
        night: nightLandings,
      },
      // MyFlightbook's documented column set has no takeoff field of its own
      // — see this file's own doc comment — so a takeoff count isn't
      // recoverable from the import; the pilot fills it in during review,
      // same as any other blank drafted cell.
      takeoffs: blankTakeoffs(),
    });
  });

  return { rows, errors };
}
